import { useNotify, UserPermission } from '@globaleris/ui.library'
import { Tab, Tabs, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCriticidad } from '../../../api/oData/enums/enumCriticidad'
import { EnumDominioTarea } from '../../../api/oData/enums/enumDominioTarea'
import { EnumEstadoTarea } from '../../../api/oData/enums/enumEstadoTarea'
import { HttpErrorProps } from '../../../hooks'
import { ObjectionTableModel } from '../../../pages/invoicesPage/invoicesPage.Model'
import { bulkUpdateObjections } from '../../../services/objections.services'
import { ObjectionsDataTable } from '../objectionsDataTable/objectionsDataTable'
import SimpleDialog from '../simpleDialog/simpleDialog'

export interface ObjectionsDialogTab {
  id: string
  label: string
  objectionsData?: ObjectionTableModel[]
  taskDomain?: EnumDominioTarea
  onRowAdd?: (row: ObjectionTableModel) => Promise<void>
  onTerminateObjection?: (row: ObjectionTableModel) => Promise<void>
  /** Called when the tab is selected - use to lazy load its data */
  onActivate?: () => void
  /** Overrides the dialog onRefresh while this tab is active */
  onRefresh?: () => void
}

interface ObjectionsDialogProps {
  open: boolean
  handleClose: () => void
  title?: string
  objDescriptions?: ObjectionTableModel[]
  objectionsData?: ObjectionTableModel[]
  showDescription?: boolean // usar campo descripcion para objeciones maestras
  showCriticality?: boolean // mostrar columna criticidad - por defecto true (se muestra)
  taskDomain?: EnumDominioTarea // dominio para rellenar combo añadir objecion
  errorProps?: HttpErrorProps & { title?: string }
  allowInsert?: boolean
  hasEditPermission?: boolean
  tableUserPermissions?: UserPermission[]
  onRowDelete?: (row: ObjectionTableModel) => Promise<void>
  onRowAdd?: (row: ObjectionTableModel) => Promise<void>
  onTerminateObjection?: (row: ObjectionTableModel) => Promise<void>
  dataTableTitle?: string
  /** Enables multiple row selection and bulk terminate toolbar button */
  canBulkTerminate?: boolean
  /** Enables individual and bulk criticality update actions */
  canModifyCriticality?: boolean
  /** Called after a successful criticality update — use to refresh parent data */
  onRefresh?: () => void
  /**
   * Optional tab definitions. With more than one tab the tabs bar is shown and the active tab
   * overrides objectionsData, taskDomain, onRowAdd and onTerminateObjection.
   * Without tabs (or with just one) the dialog behaves as a single table.
   */
  tabs?: ObjectionsDialogTab[]
}

export const ObjectionsDialog: React.FC<ObjectionsDialogProps> = ({
  open,
  handleClose,
  title,
  dataTableTitle,
  objectionsData,
  objDescriptions,
  errorProps,
  showDescription,
  showCriticality = true,
  taskDomain,
  allowInsert = false,
  hasEditPermission = false,
  tableUserPermissions,
  onRowDelete,
  onRowAdd,
  onTerminateObjection,
  canBulkTerminate = false,
  canModifyCriticality = false,
  onRefresh,
  tabs,
  ...props
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'objectionsDialog'])
  const { info, error } = useNotify()
  const [tableKey, setTableKey] = useState(0)
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const showTabs = !!tabs && tabs.length > 1
  const activeTab = tabs?.[Math.min(activeTabIndex, (tabs?.length ?? 1) - 1)]
  const refresh = activeTab?.onRefresh ?? onRefresh
  // #region Handlers Starts

  const handleTabChange = (_event: React.ChangeEvent<{}>, newIndex: number) => {
    setActiveTabIndex(newIndex)
    tabs?.[newIndex]?.onActivate?.()
  }

  const handleBulkTerminateObjections = async (selectedRows: ObjectionTableModel[]) => {
    const ids = selectedRows.map(r => r.id!)
    const response = await bulkUpdateObjections({
      objectionIds: ids,
      state: EnumEstadoTarea.Finalizada
    })
    if (!response.success) {
      error(t('objectionsDialog:bulkTerminate.error'))
    } else {
      info(t('objectionsDialog:bulkTerminate.success'))
      setTableKey(k => k + 1)
      refresh?.()
    }
  }

  const handleUpdateCriticality = async (
    rows: ObjectionTableModel[],
    criticality: EnumCriticidad
  ) => {
    const ids = rows.map(r => r.id!)
    const response = await bulkUpdateObjections({ objectionIds: ids, criticality })
    if (!response.success) {
      error(t('objectionsDialog:updateCriticality.error'))
    } else {
      info(t('objectionsDialog:updateCriticality.success'))
      setTableKey(k => k + 1)
      refresh?.()
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  useEffect(() => {
    if (open) {
      setActiveTabIndex(0)
    }
  }, [open])

  // #endregion Hooks Ends

  return (
    <SimpleDialog open={open} handleClose={handleClose} title={title} {...props}>
      {showTabs && (
        <Tabs
          value={Math.min(activeTabIndex, tabs!.length - 1)}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          {tabs!.map(tab => (
            <Tab key={tab.id} label={tab.label} />
          ))}
        </Tabs>
      )}
      <ObjectionsDataTable
        key={`${tableKey}-${activeTab?.id ?? ''}`}
        title={dataTableTitle ? <Typography variant="h6">{dataTableTitle}</Typography> : ''}
        objDescriptions={objDescriptions ?? []}
        objectionsData={activeTab ? activeTab.objectionsData : objectionsData}
        errorProps={errorProps}
        allowInsert={allowInsert}
        showDescription={showDescription}
        showCriticality={showCriticality}
        taskDomain={activeTab?.taskDomain ?? taskDomain}
        hasEditPermission={hasEditPermission}
        tableUserPermissions={tableUserPermissions}
        onRowDelete={onRowDelete}
        onRowAdd={activeTab?.onRowAdd ?? onRowAdd}
        onTerminateObjection={activeTab?.onTerminateObjection ?? onTerminateObjection}
        onBulkTerminate={canBulkTerminate ? handleBulkTerminateObjections : undefined}
        onUpdateCriticality={canModifyCriticality ? handleUpdateCriticality : undefined}
      />
    </SimpleDialog>
  )
}
